import {Observable, Subject} from "rxjs";
import {map, switchMap} from "rxjs/operators";
import {from} from "rxjs/internal/observable/from";
import {InvalidEchoError} from "../errors/invalid-echo-error";

export type SequenceParams = {
  self: any;
  args: any[];
};

export function sequence<T>() {
  return function <T> (target, propertyKey: string, descriptor: PropertyDescriptor) { // eslint-disable-line func-names
    if (descriptor === undefined) {
      descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
    }

    const method: (...args) => Observable<T> = descriptor.value; // warning: not bound

    const pending: Subject<SequenceParams> = new Subject<SequenceParams>();
    const pipedPending = pending.pipe(switchMap(({self, args}) => method.apply(self, args)));
    pending.subscribe();

    let id = 0;

    descriptor.value = function (...args): Observable<T> { // eslint-disable-line func-names
      const output: Subject<T> = new Subject<T>();

      id += 1;
      const myId = id;

      output.subscribe(() => { output.complete(); });

      pipedPending
        .pipe(map((obj) => {
          if (id === myId) return obj;
          throw new InvalidEchoError();
        }))
        .subscribe(output);

      pending.next({self: this, args});

      return output.asObservable();
    };

    return descriptor;
  };
}

export function sequenceAsync<T>() {
  return function <T> (target, propertyKey: string, descriptor: PropertyDescriptor) { // eslint-disable-line func-names
    if (descriptor === undefined) {
      descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
    }

    const method: (...args) => Promise<T> = descriptor.value; // warning: not bound

    const pending: Subject<SequenceParams> = new Subject<SequenceParams>();
    const pipedPending = pending.pipe(switchMap(({self, args}) => from(method.apply(self, args))));
    pending.subscribe();

    let id = 0;

    descriptor.value = function (...args): Promise<T> { // eslint-disable-line func-names
      const output: Subject<T> = new Subject<T>();

      id += 1;
      const myId = id;

      const promise = output.toPromise();

      output.subscribe(() => { output.complete(); });

      pipedPending
        .pipe(map((obj) => {
          if (id === myId) return obj;
          throw new InvalidEchoError();
        }))
        .subscribe(output);

      pending.next({self: this, args});

      return promise;
    };

    return descriptor;
  };
}
